import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiX, FiStar, FiTrendingUp, FiShield, FiZap, FiUsers, FiBarChart3, FiSmartphone, FiCloud, FiHeadphones, FiGlobe } = FiIcons;

const Features = () => {
    const plans = [
        {
            name: 'Free',
            price: '$0',
            period: 'forever',
            description: 'Perfect for individuals getting started',
            features: [
                'Up to 25 expenses per month',
                'Basic expense tracking',
                'Monthly reports',
                'Email support',
                'Mobile app access',
                '1 user account'
            ],
            limitations: [
                'No team collaboration',
                'No advanced analytics',
                'No API access',
                'No custom branding'
            ],
            popular: false,
            color: 'gray'
        },
        {
            name: 'Pro',
            price: '$2.99',
            period: 'per month',
            description: 'Ideal for professionals and small businesses',
            features: [
                'Unlimited expenses',
                'Advanced analytics & insights',
                'Custom categories & tags',
                'Export to Excel/PDF',
                'Priority email support',
                'Receipt photo capture',
                'Budget planning tools',
                'Multi-currency support',
                'Data backup & sync',
                '1 user account'
            ],
            limitations: [
                'No team collaboration',
                'No custom branding',
                'No API access'
            ],
            popular: true,
            color: 'primary'
        },
        {
            name: 'Team',
            price: '$19.99',
            period: 'per month',
            description: 'Perfect for teams and growing businesses',
            features: [
                'Everything in Pro',
                'Up to 10 team members',
                'Team expense sharing',
                'Admin dashboard',
                'Role-based permissions',
                'Team budgets & approvals',
                'Advanced reporting',
                'API access',
                'Custom branding',
                'Phone & chat support',
                'SSO integration',
                'Advanced security features'
            ],
            limitations: [],
            popular: false,
            color: 'accent'
        }
    ];

    const features = [
        {
            icon: FiTrendingUp,
            title: 'Smart Analytics',
            description: 'AI-powered insights help you understand spending patterns and make better financial decisions.'
        },
        {
            icon: FiShield,
            title: 'Bank-Level Security',
            description: '256-bit encryption, two-factor authentication, and SOC 2 compliance keep your data safe.'
        },
        {
            icon: FiZap,
            title: 'Real-time Sync',
            description: 'Instant synchronization across all devices with offline support and automatic backups.'
        },
        {
            icon: FiUsers,
            title: 'Team Collaboration',
            description: 'Share expenses, set budgets, and manage team finances with powerful collaboration tools.'
        },
        {
            icon: FiBarChart3,
            title: 'Advanced Reporting',
            description: 'Generate detailed reports with custom filters, charts, and export options.'
        },
        {
            icon: FiSmartphone,
            title: 'Mobile First',
            description: 'Native mobile apps for iOS and Android with full offline functionality.'
        },
        {
            icon: FiCloud,
            title: 'Cloud Storage',
            description: 'Secure cloud storage with automatic backups and 99.9% uptime guarantee.'
        },
        {
            icon: FiGlobe,
            title: 'Multi-Currency',
            description: 'Support for 150+ currencies with real-time exchange rates and conversion.'
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Hero Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="text-center mb-16"
                >
                    <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                        Choose Your Perfect Plan
                    </h1>
                    <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                        From individuals to enterprise teams, we have the right solution for your expense tracking needs.
                        Start free and upgrade as you grow.
                    </p>
                </motion.div>

                {/* Pricing Cards */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-20">
                    {plans.map((plan, index) => (
                        <motion.div
                            key={plan.name}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: index * 0.1 }}
                            className={`relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl border-2 ${plan.popular
                                    ? 'border-primary-500 transform scale-105'
                                    : 'border-gray-200 dark:border-gray-700'
                                } overflow-hidden`}
                        >
                            {plan.popular && (
                                <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-primary-500 to-accent-500 text-white text-center py-2 text-sm font-semibold">
                                    <SafeIcon icon={FiStar} className="inline w-4 h-4 mr-1" />
                                    Most Popular
                                </div>
                            )}

                            <div className={`p-8 ${plan.popular ? 'pt-12' : ''}`}>
                                <div className="text-center mb-8">
                                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                                        {plan.name}
                                    </h3>
                                    <div className="flex items-baseline justify-center mb-2">
                                        <span className="text-4xl font-bold text-gray-900 dark:text-white">
                                            {plan.price}
                                        </span>
                                        <span className="text-gray-500 dark:text-gray-400 ml-1">
                                            {plan.period}
                                        </span>
                                    </div>
                                    <p className="text-gray-600 dark:text-gray-300">
                                        {plan.description}
                                    </p>
                                </div>

                                <div className="space-y-4 mb-8">
                                    {plan.features.map((feature, idx) => (
                                        <div key={idx} className="flex items-center">
                                            <SafeIcon
                                                icon={FiCheck}
                                                className="w-5 h-5 text-green-500 mr-3 flex-shrink-0"
                                            />
                                            <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                                        </div>
                                    ))}

                                    {plan.limitations.map((limitation, idx) => (
                                        <div key={idx} className="flex items-center">
                                            <SafeIcon
                                                icon={FiX}
                                                className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0"
                                            />
                                            <span className="text-gray-500 dark:text-gray-400">{limitation}</span>
                                        </div>
                                    ))}
                                </div>

                                <button
                                    className={`w-full py-3 px-6 rounded-xl font-semibold transition-all duration-200 ${plan.popular
                                            ? 'bg-gradient-to-r from-primary-600 to-accent-600 text-white hover:from-primary-700 hover:to-accent-700 shadow-lg hover:shadow-xl'
                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600'
                                        }`}
                                >
                                    {plan.name === 'Free' ? 'Start Free' : 'Start Trial'}
                                </button>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Features Grid */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    className="mb-16"
                >
                    <div className="text-center mb-12">
                        <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                            Powerful Features for Everyone
                        </h2>
                        <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                            Whether you're tracking personal expenses or managing team budgets,
                            our platform has everything you need.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {features.map((feature, index) => (
                            <motion.div
                                key={feature.title}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                                className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 group"
                            >
                                <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                                    <SafeIcon icon={feature.icon} className="w-6 h-6 text-white" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                                    {feature.title}
                                </h3>
                                <p className="text-gray-600 dark:text-gray-300 text-sm">
                                    {feature.description}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* FAQ Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                    className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg"
                >
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
                        Frequently Asked Questions
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                                Can I switch plans anytime?
                            </h3>
                            <p className="text-gray-600 dark:text-gray-300 text-sm">
                                Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.
                            </p>
                        </div>

                        <div>
                            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                                Is my data secure?
                            </h3>
                            <p className="text-gray-600 dark:text-gray-300 text-sm">
                                Absolutely. We use bank-level encryption and are SOC 2 compliant with regular security audits.
                            </p>
                        </div>

                        <div>
                            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                                Do you offer refunds?
                            </h3>
                            <p className="text-gray-600 dark:text-gray-300 text-sm">
                                Yes, we offer a 30-day money-back guarantee for all paid plans.
                            </p>
                        </div>

                        <div>
                            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                                Can I export my data?
                            </h3>
                            <p className="text-gray-600 dark:text-gray-300 text-sm">
                                Yes, you can export your data anytime in multiple formats including Excel, CSV, and PDF.
                            </p>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default Features;